import RichTextEditor from 'Components/Common/RichTextEditor'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button, FormGroup, Label, Offcanvas, OffcanvasBody, OffcanvasHeader } from 'reactstrap'
import { createCampaignMilestone } from 'services/api/campaign'
import { RootState } from 'store'
import { GigType, Milestone } from 'types/campaign/collaborations'
import { MilestoneProperty } from 'types/campaign/milestones'

interface AddMilestoneProps {
    isOpen: boolean,
    onClose: () => void
    gigTypes: GigType[],
    properties: MilestoneProperty[],
    milestones: Milestone[],
    onSuccess: () => void
}

const AddMilestone: React.FC<AddMilestoneProps> = ({ isOpen, onClose, gigTypes, properties, milestones, onSuccess }) => {

    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const [submitting, setSubmitting] = useState(false);
    const {campaignId} = useParams();
    const [milestone, setMilestone] = useState<Milestone | null>(null);

    const getGigType = (gigTypeId: number) => {
        return gigTypes.find((g) => g.id === gigTypeId) || null;
    }

    useEffect(() => {
        if (isOpen) {
            let milestone: any = {
                gig_type_id: gigTypes[0].id,
                campaign_gig_ids: [],
                sort_order: (milestones.length + 1),
                content_media_types: { REELS: 1, FEED: 0, STORY: 0 },
                specifications: {
                    do: [],
                    dont: []
                },
            };
            for (let property of properties) {
                if (property.type == 'boolean') {
                    milestone.specifications[property.key] = false;
                } else if (property.type == 'list') {
                    milestone.specifications[property.key] = [];
                } else if (property.type == 'object_list') {
                    milestone.specifications[property.key] = [];
                }
            }
            milestone.specifications.cta_influencer = true;
            milestone.specifications.gig_influencer = !!gigTypes[0]?.has_gigs;
            if(gigTypes[0]?.slug === 'store_visit'){
                milestone.specifications.maximum_gigs = 1;
                milestone.specifications.gig_selection = true;
            }
            setMilestone(milestone);
        }
    }, [isOpen])

    const handleMilestoneChange = (key: string, value: any) => {
        setMilestone((prev) => {
            if (!prev) return prev;
            return { ...prev, [key]: value };
        });
    }

    useEffect(() => {
        const gigType = getGigType(milestone?.gig_type_id!);
        setMilestone((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                specifications: {
                    ...prev.specifications,
                    gig_influencer: !!gigType?.has_gigs,
                    ...(gigType?.slug === 'store_visit' ? {maximum_gigs: 1, gig_selection: true} : {})
                }
            };
        });
    }, [milestone?.gig_type_id])

    const addMilestone = async () => {
        if(!selectedAgencyId || !campaignId) return;
        try {
            setSubmitting(true);
            await createCampaignMilestone(selectedAgencyId, campaignId, milestone);
            toast.success("Milestone created successfully!");
            await onSuccess?.()
            onClose();
        } catch (error: any) {
            toast.error(error?.response?.data?.message ?? "Failed to create milestone!");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Offcanvas isOpen={isOpen} toggle={onClose} direction='end' id='add-campaign-gig-offcanvas'>
            <OffcanvasHeader className='border-bottom'>
                <span className="fw-bold">New Milestone</span>
            </OffcanvasHeader>
            <OffcanvasBody className="position-relative p-0">

                {/* Scrollable Content */}
                <div style={{ maxHeight: 'calc(100vh - 140px)', overflowY: 'auto', padding: '1rem' }}>
                    <FormGroup>
                        <Label className='form-label input-label mb-2'>
                            Milestone Brief <span className="text-danger">*</span>
                        </Label>
                        <RichTextEditor
                            placeholder="Describe your Milestone brief"
                            value={milestone?.brief ?? ''}
                            onChange={(value) => handleMilestoneChange('brief', value)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label className='form-label input-label mb-2'>
                            Milestone Type <span className="text-danger">*</span>
                        </Label>
                        <select
                            disabled={(milestone?.campaign_gig_ids?.length ?? 0) > 0}
                            className="form-select"
                            value={milestone?.gig_type_id || ''}
                            onChange={(e) => handleMilestoneChange('gig_type_id', Number(e.target.value) ? Number(e.target.value) : '')}
                        >
                            <option value="">Select Milestone Type</option>
                            {gigTypes.map((gigType) => (
                                <option key={gigType.id} value={gigType.id}>
                                    {gigType.name}
                                </option>
                            ))}
                        </select>
                    </FormGroup>
                </div>

                {/* Fixed Footer */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        borderTop: '1px solid #dee2e6',
                        background: '#fff',
                        padding: '0.75rem',
                        zIndex: 5
                    }}
                    className='d-flex justify-content-end align-items-center'
                >
                    <button className="btn btn-primary" disabled={submitting} onClick={addMilestone}>Create</button>
                </div>

            </OffcanvasBody>
        </Offcanvas>
    )
}

export default AddMilestone;